import { type Editor } from '@tiptap/core';

import { reportDestroyedEditorAccess } from '@/utils/telemetry/editor-observability';

// `isDestroyed` is the one safe liveness check: reading `editor.view` (or any
// command that touches it) throws once the editor is destroyed. An unmounted
// editor is still live (`isDestroyed` is false), so this is a destroy-safety
// check, not a mount/readiness guard. Pass `callSite` to report the first
// post-destroy hit per site per page load; omit it to keep the silent check.
export function isEditorLive(
  editor: Editor | null | undefined,
  callSite?: string
): editor is Editor {
  if (!editor) return false;
  if (editor.isDestroyed) {
    if (callSite) reportDestroyedEditorAccess(callSite);
    return false;
  }
  return true;
}

// For deferred closures (setTimeout, rAF, sockets, streams) that captured an
// editor which may be destroyed by the time they run.
export function whenEditorLive(
  editor: Editor | null | undefined,
  fn: (editor: Editor) => void
): void;
export function whenEditorLive(
  editor: Editor | null | undefined,
  callSite: string,
  fn: (editor: Editor) => void
): void;
export function whenEditorLive(
  editor: Editor | null | undefined,
  callSiteOrFn: string | ((editor: Editor) => void),
  fn?: (editor: Editor) => void
): void {
  if (typeof callSiteOrFn === 'function') {
    if (isEditorLive(editor)) {
      callSiteOrFn(editor);
    }
    return;
  }
  if (fn && isEditorLive(editor, callSiteOrFn)) {
    fn(editor);
  }
}
